/**
 * i18n setup: locale resolution (shared) + i18next + ICU + HTTP backend.
 * Initialize before first render to avoid FOUC.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import Backend from "i18next-http-backend";
import ICU from "i18next-icu";
import {
  resolveLocale,
  getCookie,
  setLocaleCookie,
  clearLocaleCookie,
  isRTL,
} from "@takelead/shared";
import { toDisplayString, looksLikeReactElement } from "@/lib/utils";

/**
 * i18next option keys that control behavior, not interpolation values.
 * These must NOT be coerced to string by the sanitizer.
 */
const I18NEXT_OPTION_KEYS = new Set([
  "defaultValue",
  "defaultValue_plural",
  "count",
  "ordinal",
  "context",
  "replace",
  "lng",
  "fallbackLng",
  "ns",
  "keySeparator",
  "nsSeparator",
  "returnObjects",
  "returnDetails",
  "joinArrays",
  "postProcess",
  "postProcessPassResolved",
  "interpolation",
  "tDescription",
  "overrideContext",
  "compatibilityJSON",
  "simplifyPluralSuffix",
  "skipInterpolation",
  "formatParams",
  "zonedDateTime",
  "timeZone",
]);

function sanitizeInterpolationValue(v: unknown): unknown {
  if (v == null) return v;
  const kind = typeof v;
  if (kind === "string" || kind === "number" || kind === "boolean") return v;
  if (kind === "bigint") return String(v);
  if (v instanceof Date) return v;
  if (Array.isArray(v)) return v.map(sanitizeInterpolationValue);
  if (kind === "object") return toDisplayString(v, "");
  return v;
}

function sanitizeTOptions(
  options: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (options == null || typeof options !== "object") return options;
  const keys = Object.keys(options);
  // Fast path: skip copy when all keys are i18next options (no interpolation values)
  if (keys.every((k) => I18NEXT_OPTION_KEYS.has(k))) return options;
  const out: Record<string, unknown> = { ...options };
  for (const key of keys) {
    if (I18NEXT_OPTION_KEYS.has(key)) continue;
    out[key] = sanitizeInterpolationValue(out[key]);
  }
  return out;
}

let translationApiWrapped = false;

/**
 * When `returnObjects` is false, i18next may still resolve a nested JSON object to a plain
 * object. Passing that into JSX triggers React reconciliation errors
 * ("Cannot convert object to primitive value"). Normalize to display-safe strings.
 * With `returnObjects: true`, arrays are still flattened to display-safe entries (strings/numbers);
 * nested objects in locale JSON must not reach React as raw children.
 */
/** Exported for useSafeTranslation (defense-in-depth if t() bypasses i18n.t wrap). */
export function normalizeTranslationResultForDisplay(
  result: unknown,
  returnObjects: boolean
): unknown {
  if (returnObjects) {
    if (Array.isArray(result)) {
      return result.map((item) => normalizeTranslationResultForDisplay(item, false));
    }
    if (result == null) return result;
    if (result instanceof Date) return result.toISOString();
    if (typeof result === "object" && !looksLikeReactElement(result)) {
      return toDisplayString(result);
    }
    return result;
  }
  if (result == null) return "";
  if (typeof result === "string") return result;
  if (typeof result === "number" && Number.isFinite(result)) return result;
  if (typeof result === "boolean") return result ? "true" : "false";
  if (typeof result === "bigint") return String(result);
  if (typeof result === "symbol") return result.description ?? "";
  if (typeof result === "function") return toDisplayString(result);
  if (Array.isArray(result)) {
    return result.map((item) => normalizeTranslationResultForDisplay(item, false));
  }
  if (typeof result === "object") {
    if (result instanceof Date) return result.toISOString();
    if (looksLikeReactElement(result)) return "";
    return toDisplayString(result);
  }
  return result;
}

/** Exported for useSafeTranslation. */
export function extractReturnObjects(options: unknown): boolean {
  if (options == null || typeof options !== "object" || Array.isArray(options)) return false;
  return (options as Record<string, unknown>).returnObjects === true;
}

/**
 * Wrap i18n.t() to sanitize interpolation values and catch ICU/formatjs errors.
 * Prevents "Cannot convert object to primitive value" from crashing React renders.
 */
function wrapTFunction(instance: typeof i18n): void {
  const raw = instance.t.bind(instance);
  const wrapped = (key: unknown, a2?: unknown, a3?: unknown) => {
    try {
      let result: unknown;
      let returnObjects = false;

      if (a2 === undefined && a3 === undefined) {
        result = raw(key as never);
      } else if (a3 === undefined) {
        if (typeof a2 === "string") {
          result = raw(key as never, a2 as never);
        } else if (a2 !== null && typeof a2 === "object" && !Array.isArray(a2)) {
          const o = sanitizeTOptions(a2 as Record<string, unknown>) as Record<string, unknown>;
          returnObjects = extractReturnObjects(o);
          result = raw(key as never, o as never);
        } else {
          result = raw(key as never, a2 as never);
        }
      } else if (a3 !== null && typeof a3 === "object" && !Array.isArray(a3)) {
        const o = sanitizeTOptions(a3 as Record<string, unknown>) as Record<string, unknown>;
        returnObjects = extractReturnObjects(o);
        result = raw(key as never, a2 as never, o as never);
      } else {
        result = raw(key as never, a2 as never, a3 as never);
      }

      return normalizeTranslationResultForDisplay(result, returnObjects);
    } catch (err) {
      const keyStr = Array.isArray(key)
        ? toDisplayString(key[0], "")
        : toDisplayString(key ?? "", "");
      console.error("[i18n] t() failed, fallback key:", keyStr, err);
      return keyStr;
    }
  };
  instance.t = wrapped as unknown as typeof instance.t;
}

/**
 * react-i18next nutzt oft getFixedT(ns) → eigene t-Referenz; die muss dieselbe
 * Normalisierung wie i18n.t haben (sonst Umweg ohne wrap).
 */
function wrapGetFixedT(instance: typeof i18n): void {
  const rawGf = instance.getFixedT.bind(instance);
  instance.getFixedT = ((lng?: unknown, ns?: unknown, keyPrefix?: unknown) => {
    const inner = rawGf(lng as never, ns as never, keyPrefix as never);
    return ((key: unknown, a2?: unknown, a3?: unknown) => {
      try {
        let returnObjects = false;
        let opts2 = a2;
        let opts3 = a3;
        if (a3 !== null && typeof a3 === "object" && !Array.isArray(a3)) {
          const o = sanitizeTOptions(a3 as Record<string, unknown>) as Record<string, unknown>;
          returnObjects = extractReturnObjects(o);
          opts3 = o;
        } else if (a2 !== null && typeof a2 === "object" && !Array.isArray(a2)) {
          const o = sanitizeTOptions(a2 as Record<string, unknown>) as Record<string, unknown>;
          returnObjects = extractReturnObjects(o);
          opts2 = o;
        }
        const out = inner(key as never, opts2 as never, opts3 as never);
        return normalizeTranslationResultForDisplay(out, returnObjects);
      } catch (e) {
        console.error("[i18n] getFixedT wrapper failed:", e);
        return "";
      }
    }) as unknown as typeof inner;
  }) as typeof instance.getFixedT;
}

function wrapTranslationApi(instance: typeof i18n): void {
  if (translationApiWrapped) return;
  translationApiWrapped = true;
  wrapTFunction(instance);
  wrapGetFixedT(instance);
}

function getInitialLocale(): string {
  const urlParam =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("locale")
      : null;
  const cookieLocale = getCookie("takelead_locale");
  const browserLocales =
    typeof navigator !== "undefined"
      ? [...(navigator.languages?.length ? navigator.languages : [navigator.language ?? "en"])]
      : [];
  return resolveLocale({
    cookieLocale,
    urlParam,
    browserLocales,
  });
}

const initialLocale = typeof window !== "undefined" ? getInitialLocale() : "en";

/**
 * How long a locale bundle may take before we give up on it.
 *
 * Unbounded, a hanging bundle is not a slow translation — it is a dead app: initI18n()
 * awaits these fetches BEFORE the first render, so the user stares at a blank page, and in
 * the language switcher the same hang makes the control look broken. Falling back to English
 * is a visible, recoverable outcome; waiting forever is not.
 */
const LOCALE_FETCH_TIMEOUT_MS = 5000;

/** Pre-load a namespace JSON for a locale to seed the i18next store (avoids __store null). */
async function loadNamespace(
  lng: string,
  ns: string
): Promise<Record<string, unknown>> {
  const url = `/locales/${lng}/${ns}.json`;
  try {
    const res = await fetch(url, { signal: AbortSignal.timeout(LOCALE_FETCH_TIMEOUT_MS) });
    if (!res.ok) return {};
    const text = await res.text();
    try {
      const data = JSON.parse(text) as Record<string, unknown>;
      return data ?? {};
    } catch (e) {
      console.error(`[i18n] Invalid JSON: ${url}`, e instanceof Error ? e.message : e);
      return {};
    }
  } catch (e) {
    console.error(`[i18n] Load failed: ${url}`, e instanceof Error ? e.message : e);
    return {};
  }
}

/** Call before app render. Resolves when first locale bundle is loaded. */
export async function initI18n(): Promise<void> {
  const lng = initialLocale;
  const [common, billing] = await Promise.all([
    loadNamespace(lng, "common"),
    loadNamespace(lng, "billing"),
  ]);
  const resources: { [lng: string]: { common?: Record<string, unknown>; billing?: Record<string, unknown> } } = {
    [lng]: { common, billing },
  };
  if (lng !== "en") {
    const [enCommon, enBilling] = await Promise.all([
      loadNamespace("en", "common"),
      loadNamespace("en", "billing"),
    ]);
    resources.en = { common: enCommon, billing: enBilling };
  }

  // Plugins before .init(): Backend/ICU for loading, initReactI18next binds this instance to React.
  await i18n
    .use(Backend)
    .use(ICU)
    .use(initReactI18next)
    .init({
      lng,
      fallbackLng: "en",
      ns: ["common", "billing"],
      defaultNS: "common",
      resources,
      // AUDIT-I18N: without this flag i18next's loadResources short-circuits
      // (`if (!this.options.resources || this.options.partialBundledLanguages)`)
      // whenever `resources` is set — the Backend below is then never consulted
      // and every one of the 28 locales renders through fallbackLng "en".
      partialBundledLanguages: true,
      initImmediate: false,
      returnObjects: false,
      returnNull: false,
      returnedObjectHandler: () => "",
      react: {
        useSuspense: false,
      },
      backend: {
        loadPath: "/locales/{{lng}}/{{ns}}.json",
      },
      interpolation: {
        escapeValue: false,
      },
    });

  // After init(), store must be set — useTranslation() depends on it.
  if (i18n.store == null) {
    throw new Error(
      "[i18n] store not set after init – useTranslation will crash. Check for duplicate i18next/react-i18next instances or init failure."
    );
  }

  syncDocLocale();
  setLocaleCookie(i18n.language);
  wrapTranslationApi(i18n);
}

function syncDocLocale(): void {
  if (typeof document === "undefined") return;
  const lang = toDisplayString(i18n.language ?? "en");
  document.documentElement.lang = lang;
  document.documentElement.dir = isRTL(lang) ? "rtl" : "ltr";
}

export function getResolvedLocale(): string {
  return i18n.language || initialLocale;
}

/**
 * Load a locale's bundles BEFORE switching to it.
 *
 * Not optional: i18next-icu memoizes compiled messages by `lng.ns.key` and caches
 * entries that resolved through fallbackLng. If changeLanguage() fires while the
 * bundle is still in flight, every key painted during that window is memoized as
 * English for the new locale and stays English until a full reload — a partially
 * translated UI, which is harder to notice than a fully English one.
 * Failures are swallowed: a missing bundle must fall back to English, not block
 * the switch (document.lang/dir and the cookie still need to be applied).
 */
async function preloadLocale(locale: string): Promise<void> {
  try {
    // i18next's own Backend fetch carries no signal we control, so the bound has to be a
    // race: a hanging bundle must degrade to English rather than leave the switcher dead.
    await Promise.race([
      i18n.loadLanguages(locale),
      new Promise<void>((resolve) => setTimeout(resolve, LOCALE_FETCH_TIMEOUT_MS)),
    ]);
  } catch (e) {
    console.error(`[i18n] Preload failed for ${locale}`, e instanceof Error ? e.message : e);
  }
}

/**
 * Applies a locale choice: updates i18n, document.lang/dir, and cookie.
 * - Explicit locale (e.g. "de"): set cookie and switch UI.
 * - Empty/null "Automatisch": clear cookie and use browser detection so next load and current session follow browser.
 */
export async function applyLocale(locale: string | null): Promise<string> {
  if (locale == null || locale === "") {
    clearLocaleCookie();
    const urlParam =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("locale")
        : null;
    const browserLocales =
      typeof navigator !== "undefined"
        ? [...(navigator.languages?.length ? navigator.languages : [navigator.language ?? "en"])]
        : [];
    const resolved = resolveLocale({ cookieLocale: null, urlParam, browserLocales });
    await preloadLocale(resolved);
    await i18n.changeLanguage(resolved);
    syncDocLocale();
    return resolved;
  }

  setLocaleCookie(locale);
  await preloadLocale(locale);
  await i18n.changeLanguage(locale);
  syncDocLocale();
  return locale;
}

export default i18n;
